// Suchfeld (Handoff §4): Lupe links, Ergebnisliste darunter, nach Auswahl grüner Haken + X zum Entfernen.
// Reine Darstellung: Abfrage, Entprellung und limit bleiben im jeweiligen Formular. Der Baustein
// zeigt die Liste nur, wenn das Formular sie offen hält, der Begriff die Mindestlänge erreicht
// und es Treffer (oder einen Leer-Text aus dem Code) gibt.
import { useEffect, useId, useRef, useState, type ChangeEvent, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { CheckCircle, CircleNotch, MagnifyingGlass, X, type Icon } from '@phosphor-icons/react';
import { FormFeld } from './FormFeld';
import { LISTE_ABSTAND_UNTEN, OptionZeile } from './FormAuswahl';
import { suchbegriffReicht } from './form-logik';
import { FELD, FORM } from './form-stil';

export interface FormSucheTreffer {
  id: string;
  label: string;
  unter?: string;
  icon?: Icon;
  iconFarbe?: string;
  iconFlaeche?: string;
}

export interface FormSucheProps {
  label: string;
  wert: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  treffer: readonly FormSucheTreffer[];
  onWaehlen: (id: string) => void;
  /** Liste offen (Zustand des Formulars, z. B. isOwnerOpen). */
  offen: boolean;
  /** Mindestlänge des Suchbegriffs – exakt wie im Formular. */
  mindestLaenge?: number;
  /** Prüft das Formular die Länge nach trim()? */
  trimmen?: boolean;
  /** Etwas ist gewählt → Haken + Entfernen-Knopf. */
  ausgewaehlt?: boolean;
  onEntfernen?: () => void;
  /** Escape schließt die offene Liste (statt des Formulars). */
  onSchliessen?: () => void;
  /** Text bei 0 Treffern – nur, wenn der Code einen hat. */
  leerText?: string;
  laedt?: boolean;
  platzhalter?: string;
  pflicht?: boolean;
  fehler?: boolean;
  tip?: string;
  name?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Eigene Tastenbehandlung des Formulars (z. B. Enter übernimmt eine E-Mail-Adresse). */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** Hinweiszeile unter dem Feld (Text aus dem Code). */
  hinweis?: ReactNode;
  /** Nach Auswahl nicht weitertippen (bisher stand dort ein fester Chip); ändern nur über „Auswahl entfernen“. */
  festNachAuswahl?: boolean;
}

/** Soll die Trefferliste sichtbar sein? (rein, getestet) */
export function sucheListeSichtbar(p: { offen: boolean; wert: string; mindestLaenge: number; trimmen?: boolean; trefferAnzahl: number; leerText?: string }): boolean {
  if (!p.offen || !suchbegriffReicht(p.wert, p.mindestLaenge, p.trimmen)) return false;
  return p.trefferAnzahl > 0 || !!p.leerText;
}

export function FormSuche({
  label, wert, onChange, treffer, onWaehlen, offen, mindestLaenge = 1, trimmen, ausgewaehlt, onEntfernen, onSchliessen, leerText, laedt,
  platzhalter, pflicht, fehler, tip, name, onFocus, onBlur, onKeyDown, hinweis, festNachAuswahl,
}: FormSucheProps) {
  const id = useId();
  const [aktiv, setAktiv] = useState(-1);
  const sichtbar = sucheListeSichtbar({ offen, wert, mindestLaenge, trimmen, trefferAnzahl: treffer.length, leerText });
  const listeRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (sichtbar) listeRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [sichtbar]);
  const tastatur = (e: KeyboardEvent<HTMLInputElement>) => {
    if (sichtbar) {
      if (e.key === 'Escape' && onSchliessen) { e.preventDefault(); e.stopPropagation(); onSchliessen(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setAktiv(i => Math.min(treffer.length - 1, i + 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAktiv(i => Math.max(0, i - 1)); return; }
      if (e.key === 'Enter' && aktiv >= 0 && treffer[aktiv]) { e.preventDefault(); onWaehlen(treffer[aktiv].id); setAktiv(-1); return; }
    }
    onKeyDown?.(e);
  };
  const rechts = (
    <>
      {laedt && <CircleNotch aria-hidden size={15} weight="bold" color={FORM.text2} className="pille-dreh shrink-0" />}
      {ausgewaehlt && <CheckCircle aria-label="Ausgewählt" size={17} weight="fill" color={FORM.ok} className="pf-pop shrink-0" />}
      {ausgewaehlt && onEntfernen && (
        <button type="button" aria-label="Auswahl entfernen" data-tip="Auswahl entfernen"
          onMouseDown={(e) => { e.preventDefault(); onEntfernen(); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntfernen(); } }}
          className="flex shrink-0 cursor-pointer items-center justify-center rounded-full border-none"
          style={{ width: 22, height: 22, background: 'rgba(255,255,255,0.12)', color: FORM.text }}>
          <X size={10} weight="bold" />
        </button>
      )}
    </>
  );
  return (
    <div className="flex flex-col">
      <FormFeld label={label} value={wert} onChange={(e) => { setAktiv(-1); onChange(e); }} name={name} placeholder={platzhalter}
        icon={MagnifyingGlass} pflicht={pflicht} fehler={fehler} tip={tip} rechts={rechts} onFocus={onFocus} onBlur={onBlur}
        onKeyDown={tastatur} autoComplete="off" hinweis={hinweis} readOnly={festNachAuswahl && ausgewaehlt} ariaSteuert={sichtbar ? `${id}-liste` : undefined}
        ariaAktiv={sichtbar && aktiv >= 0 ? `${id}-${aktiv}` : undefined} />
      {sichtbar && (
        <div ref={listeRef} id={`${id}-liste`} role="listbox" aria-label={label} className="pf-drop pf-scroll overflow-y-auto"
          style={{ scrollMarginBottom: LISTE_ABSTAND_UNTEN, marginTop: 6, maxHeight: 208, borderRadius: 13, padding: 4, background: FELD.liste, boxShadow: FELD.listeRing }}>
          {treffer.map((t, i) => (
            <OptionZeile key={t.id} id={`${id}-${i}`} option={t} an={false} aktiv={i === aktiv} onWaehlen={() => onWaehlen(t.id)} />
          ))}
          {treffer.length === 0 && leerText && (
            <div style={{ padding: 14, textAlign: 'center', fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>{leerText}</div>
          )}
        </div>
      )}
    </div>
  );
}
